import {
  chunkPairs,
  dimLabelColor,
  assignDistinctSpecColors,
  getSpecColor,
  normalizeSpecKey,
} from "../../../utils/specColors";

export type SpecDisplayEntry = { label: string; value: string; color: string };

/** PDF typography for specs and dimensions (readable on print). */
export const pdfSpecTextStyle = {
  fontSize: 9,
  fontWeight: "bold" as const,
};

export const pdfDimTextStyle = {
  fontSize: 9,
  fontWeight: "bold" as const,
};

export const pdfUomTextStyle = {
  fontSize: 9,
  fontWeight: "bold" as const,
};

export function formatSpecLineText(entry: SpecDisplayEntry): string {
  const value = String(entry.value ?? "").trim();
  const label = String(entry.label ?? "").trim();
  if (value && label) return `${label} - ${value}`;
  return value || label;
}

function specsArrayToObjectAndOrder(arr: unknown[]): {
  specs: Record<string, unknown>;
  order: string[];
} {
  const specs: Record<string, unknown> = {};
  const order: string[] = [];
  for (const item of arr) {
    if (!item || typeof item !== "object") continue;
    const m = item as Record<string, unknown>;
    const title = String(m.title ?? m.name ?? m.key ?? "").trim();
    if (!title) continue;
    let val: unknown;
    if (m.value !== undefined && m.value !== null && m.value !== "") {
      val = m.value;
    } else if (m.name !== undefined && m.name !== null && m.name !== "") {
      val = m.name;
    } else {
      continue;
    }
    if (!(title in specs)) order.push(title);
    specs[title] = val;
  }
  return { specs, order };
}

function specsArrayToObject(arr: unknown[]): Record<string, unknown> {
  return specsArrayToObjectAndOrder(arr).specs;
}

export function specArrayHasDuplicateTitles(arr: unknown[]): boolean {
  const seen = new Set<string>();
  for (const item of arr) {
    if (!item || typeof item !== "object") continue;
    const m = item as Record<string, unknown>;
    const title = String(m.title ?? m.name ?? m.key ?? "").trim();
    if (!title) continue;
    const norm = normalizeSpecKey(title);
    if (seen.has(norm)) return true;
    seen.add(norm);
  }
  return false;
}

function valueFromSpecArrayItem(m: Record<string, unknown>): unknown {
  if (m.value !== undefined && m.value !== null && m.value !== "") return m.value;
  if (m.name !== undefined && m.name !== null && m.name !== "") return m.name;
  return undefined;
}

/** One row per array item (supports duplicate titles). */
export function entriesFromSpecificationsArray(
  raw: unknown[]
): Array<[string, unknown]> {
  const rows: Array<[string, unknown]> = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const m = item as Record<string, unknown>;
    const title = String(m.title ?? m.name ?? m.key ?? "").trim();
    if (!title || isExcludedSpecKey(title)) continue;
    const val = valueFromSpecArrayItem(m);
    if (val === undefined || val === null || val === "") continue;
    rows.push([title, val]);
  }
  return rows;
}

/** Catalog / product-tab order: explicit specOrder, then array order, then object key order. */
export function resolveSpecTitleOrder(
  specs: Record<string, unknown>,
  options?: { specOrder?: unknown; catalogTitles?: string[] }
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  const add = (title: string) => {
    const t = String(title || "").trim();
    if (!t || seen.has(t)) return;
    if (!Object.prototype.hasOwnProperty.call(specs, t)) return;
    seen.add(t);
    result.push(t);
  };
  if (Array.isArray(options?.specOrder)) {
    options!.specOrder!.forEach((t) => add(String(t)));
  }
  if (options?.catalogTitles?.length) {
    options.catalogTitles.forEach((t) => add(t));
  }
  Object.keys(specs).forEach((k) => add(k));
  return result;
}

const isExcludedSpecKey = (key: string): boolean => {
  const k = normalizeSpecKey(key);
  if (k === "place") return true;
  return (
    k === "nool_width" ||
    k === "nool_height" ||
    k === "nool_length" ||
    k === "noolwidth" ||
    k === "noolheight" ||
    k === "noollength"
  );
};

/** Spec key/value pairs in stable product-tab order (excludes place and nool dims). */
export function orderedSpecificationEntries(
  line: Record<string, unknown>
): Array<[string, unknown]> {
  const raw = line.specifications;
  if (Array.isArray(raw)) {
    return entriesFromSpecificationsArray(raw);
  }
  const specs = parseSpecifications(raw);
  const order = resolveSpecTitleOrder(specs, { specOrder: line.specOrder });
  const rows: Array<[string, unknown]> = [];
  for (const key of order) {
    if (isExcludedSpecKey(key)) continue;
    const value = specs[key];
    if (value === undefined || value === null || value === "") continue;
    rows.push([key, value]);
  }
  return rows;
}

/** Normalize specifications from object, JSON string, or catalog-style array. */
export function parseSpecifications(raw: unknown): Record<string, unknown> {
  if (raw == null || raw === "") return {};
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      return parseSpecifications(parsed);
    } catch {
      return {};
    }
  }
  if (Array.isArray(raw)) return specsArrayToObject(raw);
  if (typeof raw === "object") return { ...(raw as Record<string, unknown>) };
  return {};
}

export function isDimNoolSpecKey(key: string): boolean {
  const k = normalizeSpecKey(key);
  return (
    k === "nool_width" ||
    k === "nool_height" ||
    k === "nool_length" ||
    k === "noolwidth" ||
    k === "noolheight" ||
    k === "noollength"
  );
}

export function getProductPlaceValue(p: Record<string, unknown>): string {
  const direct = String(p.place ?? "").trim();
  if (direct) return direct;
  const specs = parseSpecifications(p.specifications);
  for (const [key, value] of Object.entries(specs)) {
    if (normalizeSpecKey(key) !== "place") continue;
    const v = formatSpecValue(value);
    if (v) return String(v).trim();
  }
  return "";
}

/** Ensure line items from API/DB have object specs and synced `place` for PDF + details. */
export function normalizeOrderLineProduct(p: unknown): Record<string, unknown> {
  if (!p || typeof p !== "object") return {};
  const line = { ...(p as Record<string, unknown>) };
  const rawSpecs = line.specifications;
  let specOrder: string[] = Array.isArray(line.specOrder)
    ? (line.specOrder as unknown[]).map((t) => String(t).trim()).filter(Boolean)
    : [];

  if (Array.isArray(rawSpecs)) {
    if (specArrayHasDuplicateTitles(rawSpecs)) {
      line.specifications = rawSpecs;
      if (!specOrder.length) {
        specOrder = entriesFromSpecificationsArray(rawSpecs).map(([t]) => t);
      }
    } else {
      const parsed = specsArrayToObjectAndOrder(rawSpecs);
      line.specifications = parsed.specs;
      if (!specOrder.length) specOrder = parsed.order;
    }
  } else {
    line.specifications = parseSpecifications(rawSpecs);
    if (!specOrder.length) {
      specOrder = Object.keys(line.specifications as Record<string, unknown>);
    }
  }
  if (specOrder.length) line.specOrder = specOrder;

  const place = getProductPlaceValue(line);
  if (place) line.place = place;
  return line;
}

const formatSpecValue = (value: unknown) =>
  typeof value === "object" && value !== null && "name" in value
    ? String((value as { name?: string }).name ?? "")
    : String(value ?? "");

const isNumericDimNoolSpec = (key: string, value: unknown) => {
  const k = normalizeSpecKey(key);
  if (k !== "nool_width" && k !== "nool_height" && k !== "noolwidth" && k !== "noolheight") {
    return false;
  }
  const raw =
    typeof value === "object" && value !== null && "name" in value
      ? (value as { name?: unknown }).name
      : value;
  const n = parseFloat(String(raw));
  return raw !== "" && raw != null && !Number.isNaN(n);
};

/** Catalog title → color map from product definition (optional). */
export function buildCatalogColorMap(catalogSpecs?: Array<{ title?: string; color?: string }>) {
  const map: Record<string, string> = {};
  (catalogSpecs || []).forEach((s) => {
    const t = String(s.title || "").trim();
    if (!t) return;
    map[normalizeSpecKey(t)] = getSpecColor(t, s.color);
  });
  return map;
}

export function getDisplaySpecEntries(
  p: Record<string, unknown>,
  catalogColors?: Record<string, string>
): SpecDisplayEntry[] {
  const entries: SpecDisplayEntry[] = [];
  const colorFor = (label: string) => {
    const norm = normalizeSpecKey(label);
    return catalogColors?.[norm] ?? getSpecColor(label);
  };

  const rawSpecs = p.specifications;
  if (Array.isArray(rawSpecs)) {
    for (const [key, value] of entriesFromSpecificationsArray(rawSpecs)) {
      if (isNumericDimNoolSpec(key, value)) continue;
      const display = formatSpecValue(value);
      if (!display) continue;
      entries.push({ label: key, value: display, color: colorFor(key) });
    }
    return finalizeSpecDisplayColors(entries);
  }

  const specs = parseSpecifications(rawSpecs);
  const catalogTitles = Array.isArray(p.catalogSpecTitles)
    ? (p.catalogSpecTitles as unknown[]).map((t) => String(t))
    : undefined;
  const order = resolveSpecTitleOrder(specs, {
    specOrder: p.specOrder,
    catalogTitles,
  });

  for (const key of order) {
    const value = specs[key];
    if (normalizeSpecKey(key) === "place") continue;
    if (isNumericDimNoolSpec(key, value)) continue;
    const display = formatSpecValue(value);
    if (display === undefined || display === null || display === "") continue;
    entries.push({
      label: key,
      value: display,
      color: colorFor(key),
    });
  }

  return finalizeSpecDisplayColors(entries);
}

function finalizeSpecDisplayColors(entries: SpecDisplayEntry[]): SpecDisplayEntry[] {
  const norms = entries.map((e) => normalizeSpecKey(e.label));
  const hasDuplicateLabels = norms.some((n, i) => norms.indexOf(n) !== i);
  if (hasDuplicateLabels) return entries;
  return assignDistinctSpecColors(entries);
}

export { chunkPairs, dimLabelColor, getSpecColor };
